import {ReactNode} from 'react'
import {CalendarIcon, MessageChatSquareIcon, Monitor01Icon} from '@/components/ui/icon/Icon'
import {useGetUser} from '@/features/users/queries/useGetUser'
import {NOTIFICATION_TYPE, UserNotification} from '@/features/notifications/types'
import {useTranslation} from '@/translations/i18n'

export interface NotificationDescription {
    /** Type-derived icon, shared by every surface */
    icon: ReactNode
    /** Whether the notification has no attributable person behind it */
    isSystemTriggered: boolean
    /** The triggering person's name, or '' when there is none (system) or it is still resolving */
    triggeredByPersonName: string
    /** Sender label for surfaces that show one, falling back to "Sistema" */
    senderName: string
    /** The backend's server-interpolated title, resolved for every type */
    title: string
    /** Conv-001/Conv-002 only: the line introducing the conversation name */
    conversationTitleLabel: string | null
    /** Conv-001/Conv-002 only: the conversation name, when the backend sent one */
    conversationTitle: string | null
    /** The extraData line ("{patient} • PRI n°{code}"), or null when there is nothing to show */
    subtitle: {patientName?: string; projectCode?: string; repeatedAgendaDetail?: string} | null
}

/**
 * Single definition of how a notification describes itself, shared by the notification list
 * and the work-context bar (FR-010).
 *
 * Returns the discrete parts rather than a finished string: the list stacks them across
 * several lines while the bar renders one line, so each surface composes its own layout.
 *
 * It is a hook, not a pure function, because socket-delivered notifications carry only the
 * triggering user's id and the name has to be resolved with a query.
 */
export const useNotificationDescription = (notification: UserNotification): NotificationDescription => {
    const {t} = useTranslation()
    const {type, title, extraData, triggeredByUser, bodyTemplate} = notification.notification

    const isSystemTriggered = !triggeredByUser?.id || triggeredByUser.id === 'system'
    const hasTriggeredByName = Boolean(triggeredByUser?.firstName || triggeredByUser?.lastName)
    // Live (socket) notifications only carry the triggering user's id, not their
    // name — resolve it here instead of showing a fake "System" sender.
    const {data: resolvedTriggeredByUser} = useGetUser({
        userId: triggeredByUser?.id,
        responseType: 'small',
        options: {enabled: !isSystemTriggered && !hasTriggeredByName}
    })
    const triggeredByPersonName = isSystemTriggered
        ? ''
        : hasTriggeredByName
          ? `${triggeredByUser?.firstName} ${triggeredByUser?.lastName}`.trim()
          : resolvedTriggeredByUser?.fullName ?? ''

    // Conv-001/Conv-002 = conversation notifications. The BE's generic `title` doesn't
    // name the conversation, so surfaces that have the room for it render a two-line
    // title (label + conversation name) from extraData.title when the BE sends one.
    const conversationTitleLabel =
        type === NOTIFICATION_TYPE['Conv-001']
            ? t('notifications:added_to_conversation_label')
            : type === NOTIFICATION_TYPE['Conv-002']
              ? t('notifications:new_message_in_conversation_label')
              : null

    // Render the subtitle only when there is something to show: internal-conversation
    // notifications have extraData without a patientName and must not leave an empty line.
    const hasSubtitle = Boolean(
        extraData && (extraData.patientName || extraData.projectCode || type === NOTIFICATION_TYPE['Age-001-Repeated'])
    )

    return {
        icon:
            type === NOTIFICATION_TYPE['Conv-001'] || type === NOTIFICATION_TYPE['Conv-002'] ? (
                <MessageChatSquareIcon size={16} />
            ) : type.startsWith('Age-') ? (
                <CalendarIcon size={16} />
            ) : (
                <Monitor01Icon size={16} />
            ),
        isSystemTriggered,
        triggeredByPersonName,
        senderName: isSystemTriggered ? t('notifications:system_sender') : triggeredByPersonName,
        title,
        conversationTitleLabel,
        conversationTitle: conversationTitleLabel && extraData?.title ? extraData.title : null,
        subtitle: hasSubtitle
            ? {
                  patientName: extraData?.patientName,
                  projectCode: extraData?.projectCode,
                  repeatedAgendaDetail: type === NOTIFICATION_TYPE['Age-001-Repeated'] ? bodyTemplate : undefined
              }
            : null
    }
}
